import React, { useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { faqs } from '../mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { HelpCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const FAQ = () => {
  const { t } = useLanguage();

  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "How does the prayer delivery process work?", "acceptedAnswer": { "@type": "Answer", "text": "Once you submit your prayer, it is added to our Weekly Sacred Scroll along with prayers from other customers. Every week, our Jerusalem team prints the scroll and personally delivers it to your chosen holy sites. At each location, the scroll is placed with care and photographed as proof of delivery. You'll receive this photo confirmation via email." }},
        { "@type": "Question", "name": "Is my prayer kept completely confidential?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. We treat every prayer with the utmost privacy and reverence. Your prayer is handled only by our dedicated team who understand the sensitive nature of this work. We never share or sell your personal information." }},
        { "@type": "Question", "name": "What will I receive after my prayer is delivered?", "acceptedAnswer": { "@type": "Answer", "text": "You'll receive a confirmation email with a photograph showing the Weekly Sacred Scroll at the holy site, along with the date and location of delivery." }},
        { "@type": "Question", "name": "How long does it take for my prayer to be delivered?", "acceptedAnswer": { "@type": "Answer", "text": "Prayers are collected throughout the week and added to our Weekly Sacred Scroll. The scroll is delivered to the holy sites every week. You'll receive your photo proof confirmation within 3-7 business days from submission." }},
        { "@type": "Question", "name": "Can I send prayers for family members or friends?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! Many people use our service for loved ones. Simply include the name(s) of those you're dedicating it to. Your prayer will be added to the Weekly Sacred Scroll and delivered to the holy sites you choose." }},
        { "@type": "Question", "name": "How can I trust that my prayer is actually being delivered?", "acceptedAnswer": { "@type": "Answer", "text": "Transparency is foundational to our service. Every week, we photograph the Sacred Scroll at each holy site as proof of delivery. These timestamped photos show your prayer at the specific location. You can also add the Individual Photo Proof option for a close-up of your specific prayer." }}
      ]
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqSchema);
    script.id = 'faq-schema';
    document.head.appendChild(script);
    return () => { const el = document.getElementById('faq-schema'); if (el) el.remove(); };
  }, []);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <SEO title="Frequently Asked Questions — Online Prayer Request to Jerusalem" description="Common questions about our online prayer request service — how it works, delivery times to Jerusalem's holy sites (Western Wall, Church of the Holy Sepulchre), photo proof, pricing, and refunds." path="/faq" />
      {/* Hero Section */}
      <div className="container mx-auto px-4 mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full shadow-xl">
              <HelpCircle className="h-12 w-12 text-white" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            {t('faq.title')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('faq.subtitle')}
          </p>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={`item-${faq.id}`} 
                className="bg-white border-2 border-gray-200 rounded-lg px-6 hover:border-amber-300 transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-amber-600 text-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed text-base">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Additional Help */}
          <Card className="mt-12 border-2 border-amber-200 bg-amber-50">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('faqPage.stillHaveQuestions')}
              </h3>
              <p className="text-gray-700 mb-6">
                {t('faqPage.stillHaveQuestionsDesc')}
              </p>
              <a 
                href="/contact" 
                className="inline-block bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
              >
                {t('faqPage.contactUs')}
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FAQ;